import type { AreaUnit } from './types'

// Application rates as growers and labels write them ("500 mL/ac", "2.5 L/ha", "3 kg/hectare"),
// and the amount they come to over an area. Only per-area rates: a rate per volume of water
// ("mL/100 L") or per plant has no area to multiply by, so it parses as null and nothing is worked out.

const HA_PER_ACRE = 0.40468564224

const AMOUNT_UNITS: Record<string, string> = {
  ml: 'mL',
  millilitre: 'mL',
  milliliter: 'mL',
  l: 'L',
  litre: 'L',
  liter: 'L',
  g: 'g',
  gram: 'g',
  kg: 'kg',
  kilogram: 'kg',
  lb: 'lb',
  pound: 'lb',
}

/** The next unit up, for amounts that come out large (5000 mL reads better as 5 L). */
const BIGGER: Record<string, { unit: string; factor: number }> = { mL: { unit: 'L', factor: 1000 }, g: { unit: 'kg', factor: 1000 } }

export type RateUnit = { amountUnit: string; per: AreaUnit }

/** `"mL/ac"` → `{ amountUnit: 'mL', per: 'acre' }`. Anything not "<amount unit> per <ac|ha>" is null. */
export function parseRateUnit(unit: string | null | undefined): RateUnit | null {
  const m = (unit ?? '')
    .trim()
    .toLowerCase()
    .match(/^([a-z]+?)s?(?:\s+(?:of\s+)?product)?\s*(?:\/|per)\s*(?:1\s+)?(ha|hectares?|ac|acres?)$/)
  if (!m) return null
  const amountUnit = AMOUNT_UNITS[m[1]!]
  if (!amountUnit) return null
  return { amountUnit, per: m[2]!.startsWith('h') ? 'hectare' : 'acre' }
}

/** Area in one unit, expressed in another. */
export function convertArea(value: number, from: AreaUnit, to: AreaUnit): number {
  if (from === to) return value
  return from === 'acre' ? value * HA_PER_ACRE : value / HA_PER_ACRE
}

/** Up to 3 decimals, no trailing zeros. */
const tidy = (n: number) => Number(n.toFixed(3))

/**
 * How much product a rate comes to over an area: `500 mL/ac × 10 ac` → `{ quantity: 5, unit: 'L' }`.
 * The area can be in either unit; the rate's own area unit wins. Null when the rate isn't per area,
 * or the numbers aren't positive.
 */
export function amountFromRate(rate: number, rateUnit: string, area: number, areaUnit: AreaUnit): { quantity: number; unit: string } | null {
  const parsed = parseRateUnit(rateUnit)
  if (!parsed || !(rate > 0) || !(area > 0)) return null
  let quantity = rate * convertArea(area, areaUnit, parsed.per)
  let unit = parsed.amountUnit
  const bigger = BIGGER[unit]
  if (bigger && quantity >= bigger.factor) {
    quantity /= bigger.factor
    unit = bigger.unit
  }
  return { quantity: tidy(quantity), unit }
}
